import React, { FC, useRef, useState, useMemo, useCallback } from 'react'
import { Flex, Spin, Button, Typography } from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import Alert from '@mui/material/Alert'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import { useVerticalOverviewQuery } from 'hooks/useVerticalOverviewQuery'
import { useVerticalEntitiesPaginatedQuery } from 'hooks/useVerticalEntitiesPaginatedQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { useDebounce } from 'hooks/useDebounce'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import { useMinYearQuery } from 'hooks/useMinYearQuery'
import { BaseTableOverview, CurrentZone } from 'utils/types'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import NoDataContainer from 'components/NoDataContainer'
import DownloadPlaybookButton from 'components/DownloadPlaybookButton'
import { VERTICAL_ROUTE_MAP } from 'config/verticalRoutes'
import { selectCurrentZone } from 'store/global'
import { strColumns, keysForEmptyColumnsRequest } from './utils'
import { STR_PAGE_SIZE } from './constants'
import { useSTRExport } from './useSTRExport'
import STRSummary from './STRSummary'
import STRExpandedRow, { buildYearlyBreakdown } from './STRExpandedRow'
import STRFilterBar, { STRFilters, convertSTRFiltersToApiFilters } from './STRFilterBar'
import STRListingMap from './STRListingMap'
import STRBanner from './STRExportBanner'
import STRActiveFilters from './STRActiveFilters'
import messages from './messages'
import tablePageStyles from 'styles/TablePageCommon.scss'
import strStyles from './STRTablePage.module.scss'

const STRTablePage: FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentZone: CurrentZone = useSelector(selectCurrentZone as any)
  const strIndices = currentZone?.strIndices

  const { sorting, setSorting } = useVerticalSearchAndSort()

  const filterPickerRef = useRef<HTMLDivElement | null>(null)
  const summaryRef = useRef<HTMLDivElement | null>(null)

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(STR_PAGE_SIZE)
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [isMapVisible, setIsMapVisible] = useState(true)
  const [filters, setFilters] = useState<STRFilters>({})
  const [searchValue, setSearchValue] = useState('')
  const [searchColumn, setSearchColumn] = useState('')
  const [allSelected, setAllSelected] = useState(false)

  const debouncedRawFilters = useDebounce(filters, 800)
  const apiFilters = useMemo(() => convertSTRFiltersToApiFilters(filters), [filters])
  const debouncedFilters = useDebounce(apiFilters, 800)

  const handleFiltersChange = useCallback((newFilters: STRFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }, [])

  const handleSearch = useCallback((value: string, column: string) => {
    setSearchValue(value)
    setSearchColumn(column)
    setCurrentPage(1)
  }, [])

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: strIndices,
    currentQueryKey: 'str-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { minYear } = useMinYearQuery({
    verticalIndices: strIndices,
    currentQueryKey: 'str-min-year-key',
  })

  const { data: overviewData, isLoading: isLoadingOverview } = useVerticalOverviewQuery<BaseTableOverview>({
    verticalIndices: strIndices,
    currentQueryKey: 'str-overview-key',
    searchValue: searchValue || undefined,
    searchColumn: searchColumn || undefined,
    filters: debouncedFilters,
  })

  const { verticalEntities, isFetching, isLoading, isError } = useVerticalEntitiesPaginatedQuery({
    verticalIndices: strIndices,
    currentQueryKey: 'str-entities-key',
    searchValue: searchValue || undefined,
    searchColumn: searchColumn || undefined,
    sorting,
    filters: debouncedFilters,
    pageSize,
    page: currentPage,
  })

  const total = overviewData?.total || 0

  const { handleExport, exportSuccessMsg, clearExportMsg, isLoadingExchangeRates } = useSTRExport({
    sorting,
    total,
    verticalIndices: strIndices,
  })

  const hasNoData = useMemo(
    () =>
      !isLoading &&
      !isFetching &&
      !isError &&
      (!verticalEntities || verticalEntities.length === 0) &&
      !searchValue &&
      debouncedFilters.length === 0,
    [isLoading, isFetching, isError, verticalEntities, searchValue, debouncedFilters],
  )

  const pageItemsCount = verticalEntities?.length || 0
  const rangeStart = pageItemsCount > 0 ? (currentPage - 1) * pageSize + 1 : 0
  const rangeEnd = pageItemsCount > 0 ? (currentPage - 1) * pageSize + pageItemsCount : 0
  const rangeLabel = pageItemsCount > 0 ? `${rangeStart}-${rangeEnd} of ${total}` : `0 of ${total}`

  return (
    <div className={tablePageStyles.container}>
      <div ref={filterPickerRef}>
        <nav id='navigationHeaderWrapper' className={tablePageStyles.navigationHeaderWrapper}>
          <NavigationHeader />
        </nav>
        <header className={`page-header withNavigationHeader ${strStyles.pageHeader}`}>
          <h1 className={strStyles.pageTitle}>
            <FormattedMessage {...messages.header} />
          </h1>
          {!hasNoData && <DownloadPlaybookButton verticalId={VERTICAL_ROUTE_MAP.str.id} />}
        </header>
      </div>

      {hasNoData ? (
        <NoDataContainer verticalName='Short Term Rentals' />
      ) : (
        <>
          <STRFilterBar
            isMapVisible={isMapVisible}
            onToggleMap={() => setIsMapVisible(prev => !prev)}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onSearch={handleSearch}
            minYear={minYear}
          />

          <STRActiveFilters
            filters={debouncedRawFilters}
            searchValue={searchValue}
            onFiltersChange={handleFiltersChange}
            onSearch={handleSearch}
          />

          <div ref={summaryRef} style={{ marginBottom: 16 }}>
            <STRSummary overviewData={overviewData} isLoading={isLoadingOverview} />
          </div>

          {isMapVisible && verticalEntities && verticalEntities.length > 0 && (
            <STRListingMap entities={verticalEntities as unknown as Record<string, unknown>[]} />
          )}

          {isLoading ? (
            <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
              <Spin size='large' />
            </Flex>
          ) : isError ? (
            <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
              Something went wrong
            </Alert>
          ) : (
            <>
              {exportSuccessMsg && (
                <STRBanner icon={<CheckCircleFilled style={{ color: '#52C41A' }} />} onClose={clearExportMsg}>
                  {exportSuccessMsg}
                </STRBanner>
              )}

              {selectedIds.length > 0 && (
                <STRBanner
                  variant='info'
                  onClose={() => {
                    setSelectedIds([])
                    setAllSelected(false)
                  }}
                >
                  <Typography.Text>
                    {allSelected ? `All ${total} cases selected` : `${selectedIds.length} cases selected`}
                  </Typography.Text>
                  {!allSelected && (
                    <Typography.Link onClick={() => setAllSelected(true)}>
                      {`Select all cases(${total})`}
                    </Typography.Link>
                  )}
                </STRBanner>
              )}

              {emptyColumnsIsFetched && (
                <VerticalTable
                  tableId='str'
                  pageSize={pageSize}
                  enableColumnPicker
                  dataSource={verticalEntities || []}
                  columns={strColumns}
                  total={total}
                  loading={isFetching}
                  disabledColumns={emptyColumnsData}
                  onSelectionChange={ids => {
                    setSelectedIds(ids)
                    if (ids.length === 0) setAllSelected(false)
                  }}
                  removeFromPickerColumns={[COLUMNS_IDS.profileNames]}
                  hideSelectAll={false}
                  stickyHeaderOffset={45}
                  paginationConfig={{
                    current: currentPage,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    hideOnSinglePage: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: totalItems => `Total ${totalItems} item${totalItems === 1 ? '' : 's'}`,
                    onChange: (newPage, newPageSize) => {
                      setCurrentPage(newPage)
                      if (newPageSize !== pageSize) {
                        setPageSize(newPageSize)
                        setCurrentPage(1)
                      }
                    },
                  }}
                  rowClassName={record => {
                    if (expandedRowKeys.length > 0) {
                      const id = (record as Record<string, unknown>).id as string
                      if (!expandedRowKeys.includes(id)) return strStyles.blurredRow
                      return strStyles.expandedRow
                    }
                    return strStyles.defaultRowClassName
                  }}
                  expandable={{
                    expandedRowRender: record => <STRExpandedRow record={record} />,
                    onExpand: (expanded, record) => {
                      const id = (record as Record<string, unknown>).id as string
                      setExpandedRowKeys(expanded ? [id] : [])
                    },
                    expandedRowKeys,
                    rowExpandable: record => {
                      const yearlyData = buildYearlyBreakdown(record)
                      return yearlyData.length > 0
                    },
                  }}
                  onRow={record => ({
                    onClick: () => {
                      const id = (record as Record<string, unknown>).id as string
                      window.open(`${window.location.origin}/vertical/short-term-rentals/${id}`, '_blank')
                    },
                  })}
                  onChange={(_pag, _filters, sorter) => {
                    if (!Array.isArray(sorter)) {
                      if (sorter.order) {
                        setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                      } else {
                        setSorting([])
                      }
                    }
                  }}
                >
                  <div className={tablePageStyles.counter}>
                    {`Host Table : `}
                    <span>{rangeLabel}</span>
                  </div>
                  <Flex gap={8} align='center'>
                    {selectedIds.length > 0 && (
                      <Button
                        variant='outlined'
                        color='default'
                        onClick={() => {
                          allSelected ? handleExport() : handleExport(selectedIds)
                          setSelectedIds([])
                          setAllSelected(false)
                        }}
                        disabled={isLoadingExchangeRates}
                      >
                        {allSelected ? `Export Selected (${total})` : `Export Selected (${selectedIds.length})`}
                      </Button>
                    )}
                    <Button
                      type='primary'
                      onClick={() => handleExport()}
                      disabled={total === 0 || isLoadingExchangeRates}
                    >
                      {`Export All (${total})`}
                    </Button>
                  </Flex>
                </VerticalTable>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default STRTablePage
